import type { ReactNode } from 'react';
import { Icon } from './Icon';
import './Chip.css';

/**
 * MVP-357: `removeLabel` used to be optional with an English `?? 'Remove'` fallback, which made the
 * only untranslated string on a Thai screen the one a screen reader actually reads out. It cannot
 * simply be made required — a plain chip (`NowPlayingCard`'s badges) renders no button and has
 * nothing to name — so the two travel together as a pair the type system enforces. Passing
 * `onRemove` without `removeLabel` is now a compile error rather than a silent English default.
 */
export type ChipProps = {
  children: ReactNode;
  variant?: 'default' | 'accent';
} & (
  | {
      /** Present -> renders a trailing remove ×, which must be named. */
      onRemove: () => void;
      /** `ui/` stays dictionary-free (see `ui/SearchField.tsx`'s rule), so the caller resolves this. */
      removeLabel: string;
    }
  | { onRemove?: undefined; removeLabel?: undefined }
);

/** DESIGN.md §9.1 `Chip`. ActiveFilterBar's removable facet-value summary (AC-UI-6) is this
 * component's main consumer. */
// `props` is read whole rather than destructured, for the same reason as `Dialog`'s: `removeLabel`'s
// type is only known once `onRemove` has been narrowed.
export function Chip(props: ChipProps) {
  const { children, variant = 'default' } = props;
  return (
    <span className={`chip${variant === 'accent' ? ' chip--accent' : ''}`}>
      <span className="text-meta truncate">{children}</span>
      {props.onRemove !== undefined && (
        <button type="button" className="chip__remove" onClick={props.onRemove} aria-label={props.removeLabel} title={props.removeLabel}>
          <Icon name="close" size={12} />
        </button>
      )}
    </span>
  );
}
